import { toast } from "sonner";
import {
  AssetPickerField,
  EquipmentEvidenceDialog,
  AdHocItemFields,
} from "@/components/equipment/EquipmentDialogs";
import { ProtocolReviewDialog } from "@/components/equipment/ProtocolReviewDialog";
import { protocolItemMeta } from "@/components/equipment/equipmentLabels";
import {
  initiateAssetHandover,
  initiateAssetReturn,
  initiateKeyCardHandover,
  initiateKeyCardReturn,
  respondEquipmentProtocol,
} from "@/lib/equipmentApi";
import type { EquipmentAsset, EquipmentProtocol, StaffEquipmentItem } from "@/types/equipment";

interface WorkerEquipmentModalsProps {
  orgId: string;
  workerId: string;
  saving: boolean;
  run: (action: () => Promise<unknown>, success: string) => Promise<void>;
  keyOpen: boolean;
  setKeyOpen: (open: boolean) => void;
  keyName: string;
  setKeyName: (value: string) => void;
  toolOpen: boolean;
  setToolOpen: (open: boolean) => void;
  assetId: string;
  setAssetId: (value: string) => void;
  availableAssets: EquipmentAsset[];
  assignedAssets: EquipmentAsset[];
  assignedKeys: StaffEquipmentItem[];
  returnItem: StaffEquipmentItem | EquipmentAsset | null;
  returnKind: "key_card" | "asset" | null;
  setReturnItem: (item: StaffEquipmentItem | EquipmentAsset | null) => void;
  setReturnKind: (kind: "key_card" | "asset" | null) => void;
  respondProtocol: EquipmentProtocol | null;
  setRespondProtocol: (protocol: EquipmentProtocol | null) => void;
}

export function WorkerEquipmentModals({
  orgId,
  workerId,
  saving,
  run,
  keyOpen,
  setKeyOpen,
  keyName,
  setKeyName,
  toolOpen,
  setToolOpen,
  assetId,
  setAssetId,
  availableAssets,
  assignedAssets,
  assignedKeys,
  returnItem,
  returnKind,
  setReturnItem,
  setReturnKind,
  respondProtocol,
  setRespondProtocol,
}: WorkerEquipmentModalsProps) {
  const selectedAsset = availableAssets.find((item) => item.id === assetId);
  const respondMeta = respondProtocol
    ? protocolItemMeta(respondProtocol, assignedAssets, assignedKeys)
    : null;

  return (
    <>
      <EquipmentEvidenceDialog
        open={keyOpen}
        title="Przekaż przedmiot"
        description="Pracownik musi potwierdzić odbiór w aplikacji."
        submitLabel="Przekaż"
        saving={saving}
        onOpenChange={(open) => {
          setKeyOpen(open);
          if (!open) {
            setKeyName("");
          }
        }}
        extra={<AdHocItemFields name={keyName} onNameChange={setKeyName} />}
        onSubmit={(notes, files) => {
          if (!keyName.trim()) {
            toast.error("Podaj nazwę przedmiotu.");
            return;
          }
          void run(async () => {
            await initiateKeyCardHandover({
              orgId,
              workerId,
              name: keyName,
              type: "other",
              conditionNotes: notes,
              photos: files,
            });
            setKeyOpen(false);
            setKeyName("");
          }, "Protokół przekazania wysłany");
        }}
      />

      <EquipmentEvidenceDialog
        open={toolOpen}
        title="Przekaż narzędzie"
        description="Wybierz wolny egzemplarz ze stanu firmy. Stan możesz poprawić przed wysłaniem."
        submitLabel="Przekaż"
        saving={saving}
        initialNotes={selectedAsset?.notes ?? ""}
        extra={
          <AssetPickerField
            options={availableAssets.map((item) => ({
              value: item.id,
              label: item.serial_number ? `${item.name} (${item.serial_number})` : item.name,
            }))}
            value={assetId}
            onChange={setAssetId}
          />
        }
        onOpenChange={(open) => {
          setToolOpen(open);
          if (!open) setAssetId("");
        }}
        onSubmit={(notes, files) => {
          const asset = availableAssets.find((item) => item.id === assetId);
          if (!asset) {
            toast.error("Wybierz narzędzie ze stanu firmy.");
            return;
          }
          void run(async () => {
            await initiateAssetHandover({
              orgId,
              workerId,
              asset,
              conditionNotes: notes,
              photos: files,
            });
            setToolOpen(false);
            setAssetId("");
          }, "Protokół przekazania wysłany");
        }}
      />

      <EquipmentEvidenceDialog
        open={Boolean(returnItem && returnKind)}
        title="Zdaj sprzęt"
        description="Druga strona musi potwierdzić zwrot."
        submitLabel="Zdaj"
        saving={saving}
        initialNotes={
          returnKind === "asset" ? (returnItem as EquipmentAsset | null)?.notes ?? "" : ""
        }
        onOpenChange={(open) => {
          if (!open) {
            setReturnItem(null);
            setReturnKind(null);
          }
        }}
        onSubmit={(notes, files) => {
          if (!returnItem || !returnKind) return;
          void run(async () => {
            if (returnKind === "key_card") {
              await initiateKeyCardReturn({
                item: returnItem as StaffEquipmentItem,
                conditionNotes: notes,
                photos: files,
              });
            } else {
              await initiateAssetReturn({
                asset: returnItem as EquipmentAsset,
                conditionNotes: notes,
                photos: files,
              });
            }
            setReturnItem(null);
            setReturnKind(null);
          }, "Protokół zwrotu wysłany");
        }}
      />

      <ProtocolReviewDialog
        protocol={respondProtocol}
        itemName={respondMeta?.name ?? ""}
        serialNumber={respondMeta?.serialNumber ?? null}
        saving={saving}
        onOpenChange={(open) => {
          if (!open) setRespondProtocol(null);
        }}
        onAccept={() => {
          if (!respondProtocol) return;
          void run(async () => {
            await respondEquipmentProtocol({ protocolId: respondProtocol.id, accept: true });
            setRespondProtocol(null);
          }, "Protokół zaakceptowany");
        }}
        onReject={(reason) => {
          if (!respondProtocol) return;
          void run(async () => {
            await respondEquipmentProtocol({
              protocolId: respondProtocol.id,
              accept: false,
              rejectionReason: reason,
            });
            setRespondProtocol(null);
          }, "Protokół odrzucony");
        }}
      />
    </>
  );
}
